import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import PolicySection from '../privacy-policy/components/PolicySection';
import TableOfContents from '../privacy-policy/components/TableOfContents';
import Footer from '../homepage-sales-landing/components/Footer';

const GDPRCompliance = () => {
  const [activeSection, setActiveSection] = useState('introduction');

  const sections = [
    {
      id: 'introduction',
      title: 'Introduction to GDPR Compliance',
      icon: 'Shield',
      content: (
        <>
          <p className="mb-4">
            This GDPR Compliance page outlines how AuthorPreneur AI, operated by Entrepreneur Power Network, LLC, complies with the General Data Protection Regulation (GDPR) and protects the personal data of users in the European Economic Area (EEA), United Kingdom, and Switzerland.
          </p>
          <p className="mb-4">
            The GDPR is one of the most comprehensive data protection laws globally, giving individuals significant control over their personal data. We are fully committed to GDPR compliance and transparent data practices.
          </p>
          <p>
            Last Updated: December 8, 2025
          </p>
        </>
      )
    },
    {
      id: 'lawful-basis',
      title: 'Lawful Basis for Processing',
      icon: 'Scale',
      content: (
        <>
          <p className="mb-6">
            Under GDPR Article 6, we process personal data based on the following lawful grounds:
          </p>

          <div className="space-y-6">
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-start space-x-3 mb-3">
                <Icon name="FileCheck" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                <h3 className="text-lg font-semibold text-foreground">1. Contractual Necessity</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Processing necessary for the performance of a contract with you, including:
              </p>
              <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
                <li>Account creation and management</li>
                <li>Subscription service delivery</li>
                <li>Payment processing and billing</li>
                <li>AI-powered book writing features</li>
                <li>Community access and engagement</li>
              </ul>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-start space-x-3 mb-3">
                <Icon name="UserCheck" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                <h3 className="text-lg font-semibold text-foreground">2. Consent</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Explicit consent you provide for specific purposes, such as:
              </p>
              <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
                <li>Marketing communications and newsletters</li>
                <li>Analytics and performance tracking</li>
                <li>Third-party integrations (Skool community)</li>
                <li>Optional cookies and tracking technologies</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-3 font-medium">
                You can withdraw consent at any time through your account settings or by contacting us.
              </p>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-start space-x-3 mb-3">
                <Icon name="Building" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                <h3 className="text-lg font-semibold text-foreground">3. Legitimate Interests</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Processing for our legitimate business interests, balanced against your rights, including:
              </p>
              <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
                <li>Platform security and fraud prevention</li>
                <li>Service improvement and feature development</li>
                <li>Customer support and communication</li>
                <li>Business analytics and reporting</li>
              </ul>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-start space-x-3 mb-3">
                <Icon name="AlertTriangle" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                <h3 className="text-lg font-semibold text-foreground">4. Legal Obligations</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Processing required to comply with legal obligations, such as:
              </p>
              <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
                <li>Tax and accounting requirements</li>
                <li>Response to legal requests and court orders</li>
                <li>Prevention of illegal activities</li>
                <li>Regulatory compliance and audits</li>
              </ul>
            </div>
          </div>
        </>
      )
    },
    {
      id: 'data-subject-rights',
      title: 'Your Data Protection Rights',
      icon: 'Users',
      content: (
        <>
          <p className="mb-6">
            Under the GDPR, you have the following rights regarding your personal data:
          </p>

          <div className="space-y-4">
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="Eye" size={20} color="var(--color-primary)" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-foreground mb-2">Right to Access (Article 15)</h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    You can request a copy of the personal data we hold about you, including:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                    <li>Categories of data being processed</li>
                    <li>Purposes of processing</li>
                    <li>Recipients of your data</li>
                    <li>Data retention periods</li>
                  </ul>
                  <p className="text-sm text-muted-foreground mt-3 font-medium">
                    Response time: Within 30 days
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="Edit" size={20} color="var(--color-primary)" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-foreground mb-2">Right to Rectification (Article 16)</h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    You can request correction of inaccurate or incomplete personal data.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Update directly in your account settings or contact us for assistance.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 bg-red-100 dark:bg-red-900/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="Trash2" size={20} color="var(--color-destructive)" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-foreground mb-2">Right to Erasure / "Right to be Forgotten" (Article 17)</h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    You can request deletion of your personal data when:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                    <li>Data is no longer necessary for original purpose</li>
                    <li>You withdraw consent and no other legal basis exists</li>
                    <li>You object to processing and there are no overriding legitimate grounds</li>
                    <li>Data has been unlawfully processed</li>
                  </ul>
                  <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mt-3">
                    <p className="text-xs text-muted-foreground">
                      <strong>Note:</strong> Some data may be retained for legal obligations, such as financial records for tax purposes.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="Ban" size={20} color="var(--color-primary)" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-foreground mb-2">Right to Restriction of Processing (Article 18)</h4>
                  <p className="text-sm text-muted-foreground">
                    You can request limitation of data processing in certain circumstances, such as when contesting data accuracy or objecting to processing.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="Download" size={20} color="var(--color-primary)" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-foreground mb-2">Right to Data Portability (Article 20)</h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    You can receive your personal data in a structured, commonly used, machine-readable format (JSON/CSV) and transmit it to another controller.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Available for data processed based on consent or contract, in an automated manner.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="XCircle" size={20} color="var(--color-primary)" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-foreground mb-2">Right to Object (Article 21)</h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    You can object to processing based on legitimate interests or for direct marketing purposes.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    We will cease processing unless we demonstrate compelling legitimate grounds that override your interests.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="UserX" size={20} color="var(--color-primary)" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-foreground mb-2">Rights Related to Automated Decision-Making (Article 22)</h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    You have the right not to be subject to decisions based solely on automated processing, including profiling, that produce legal or similarly significant effects.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Note: We do not currently engage in automated decision-making that produces legal effects.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 mt-6">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-foreground mb-2">How to Exercise Your Rights</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  To exercise any of these rights, please contact us at:
                </p>
                <p className="text-sm text-muted-foreground">
                  <strong>Email:</strong>{' '}
                  <a href="mailto:success@entrepreneurpowernetwork.com" className="text-primary hover:underline">
                    success@entrepreneurpowernetwork.com
                  </a>
                </p>
                <p className="text-sm text-muted-foreground mt-3">
                  Include "GDPR Request" in the subject line and provide verification of your identity.
                </p>
              </div>
            </div>
          </div>
        </>
      )
    },
    {
      id: 'data-collection',
      title: 'Data Collection & Processing',
      icon: 'Database',
      content: (
        <>
          <p className="mb-6">
            We collect and process the following categories of personal data in compliance with GDPR principles:
          </p>

          <div className="space-y-6">
            <div className="bg-card border border-border rounded-lg p-6">
              <h4 className="font-semibold text-foreground mb-4">Identity & Contact Data</h4>
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">Data Collected:</p>
                  <p className="text-sm text-muted-foreground">Name, email address, username</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">Purpose:</p>
                  <p className="text-sm text-muted-foreground">Account management, communication, service delivery</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">Legal Basis:</p>
                  <p className="text-sm text-muted-foreground">Contract performance, consent</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">Retention:</p>
                  <p className="text-sm text-muted-foreground">Until account deletion + 30 days</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <h4 className="font-semibold text-foreground mb-4">Financial Data</h4>
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">Data Collected:</p>
                  <p className="text-sm text-muted-foreground">Payment method details (processed by Stripe), transaction history</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">Purpose:</p>
                  <p className="text-sm text-muted-foreground">Payment processing, subscription management, fraud prevention</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">Legal Basis:</p>
                  <p className="text-sm text-muted-foreground">Contract performance, legal obligation</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">Retention:</p>
                  <p className="text-sm text-muted-foreground">7 years (tax and accounting requirements)</p>
                </div>
                <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mt-3">
                  <p className="text-xs text-muted-foreground">
                    <strong>Note:</strong> Payment data is processed by Stripe (PCI DSS Level 1 certified). We do not store full credit card details.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <h4 className="font-semibold text-foreground mb-4">Usage & Technical Data</h4>
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">Data Collected:</p>
                  <p className="text-sm text-muted-foreground">IP address, browser type, device information, cookies, page views</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">Purpose:</p>
                  <p className="text-sm text-muted-foreground">Platform security, service improvement, analytics</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">Legal Basis:</p>
                  <p className="text-sm text-muted-foreground">Legitimate interests, consent (for analytics cookies)</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">Retention:</p>
                  <p className="text-sm text-muted-foreground">26 months (Google Analytics), session logs 90 days</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <h4 className="font-semibold text-foreground mb-4">Content Data</h4>
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">Data Collected:</p>
                  <p className="text-sm text-muted-foreground">Book manuscripts, AI prompts, project details, community posts</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">Purpose:</p>
                  <p className="text-sm text-muted-foreground">Service provision, AI processing, community engagement</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">Legal Basis:</p>
                  <p className="text-sm text-muted-foreground">Contract performance, consent</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">Retention:</p>
                  <p className="text-sm text-muted-foreground">Until project deletion or account termination</p>
                </div>
              </div>
            </div>
          </div>
        </>
      )
    },
    {
      id: 'international-transfers',
      title: 'International Data Transfers',
      icon: 'Globe',
      content: (
        <>
          <p className="mb-6">
            As a U.S.-based company, we may transfer your personal data outside the European Economic Area (EEA). We ensure appropriate safeguards are in place:
          </p>

          <div className="space-y-6">
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-start space-x-3 mb-3">
                <Icon name="Shield" size={24} color="var(--color-primary)" className="flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="font-semibold text-foreground mb-2">Data Transfer Safeguards</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    We implement the following mechanisms to protect data transferred outside the EEA:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
                    <li>Standard Contractual Clauses (SCCs) approved by the European Commission</li>
                    <li>Encryption in transit (TLS 1.3) and at rest (AES-256)</li>
                    <li>Due diligence on third-party service providers</li>
                    <li>Data Processing Agreements with all processors</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <h4 className="font-semibold text-foreground mb-4">Third-Party Service Locations</h4>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">Supabase (Database & Auth)</p>
                  <p className="text-sm text-muted-foreground mb-1">
                    <strong>Location:</strong> AWS data centers (configurable region selection)
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <strong>Safeguards:</strong> EU-U.S. Data Privacy Framework participant, SCCs
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">OpenAI (ChatGPT API)</p>
                  <p className="text-sm text-muted-foreground mb-1">
                    <strong>Location:</strong> United States
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <strong>Safeguards:</strong> Data Processing Addendum, Enterprise data retention policies
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">Stripe (Payment Processing)</p>
                  <p className="text-sm text-muted-foreground mb-1">
                    <strong>Location:</strong> United States, EU data residency options
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <strong>Safeguards:</strong> PCI DSS Level 1 certified, EU-U.S. Data Privacy Framework participant
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">Google Analytics</p>
                  <p className="text-sm text-muted-foreground mb-1">
                    <strong>Location:</strong> Global data centers
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <strong>Safeguards:</strong> Google Ads Data Processing Terms, anonymization features
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Icon name="AlertCircle" size={20} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-2">Important Notice</h4>
                  <p className="text-sm text-muted-foreground">
                    While we implement robust safeguards, U.S. data transfers are subject to U.S. surveillance laws. We minimize data transfers and use encryption to protect your information.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
      )
    },
    {
      id: 'data-security',
      title: 'Data Security Measures',
      icon: 'Lock',
      content: (
        <>
          <p className="mb-6">
            We implement comprehensive technical and organizational measures to protect your personal data:
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <Icon name="Server" size={24} color="var(--color-primary)" />
                <h4 className="font-semibold text-foreground">Technical Measures</h4>
              </div>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start">
                  <Icon name="Check" size={16} className="mr-2 mt-0.5 flex-shrink-0 text-success" />
                  <span>End-to-end encryption (TLS 1.3)</span>
                </li>
                <li className="flex items-start">
                  <Icon name="Check" size={16} className="mr-2 mt-0.5 flex-shrink-0 text-success" />
                  <span>Data encryption at rest (AES-256)</span>
                </li>
                <li className="flex items-start">
                  <Icon name="Check" size={16} className="mr-2 mt-0.5 flex-shrink-0 text-success" />
                  <span>Regular security audits and penetration testing</span>
                </li>
                <li className="flex items-start">
                  <Icon name="Check" size={16} className="mr-2 mt-0.5 flex-shrink-0 text-success" />
                  <span>Web Application Firewall (WAF)</span>
                </li>
                <li className="flex items-start">
                  <Icon name="Check" size={16} className="mr-2 mt-0.5 flex-shrink-0 text-success" />
                  <span>DDoS protection and rate limiting</span>
                </li>
                <li className="flex items-start">
                  <Icon name="Check" size={16} className="mr-2 mt-0.5 flex-shrink-0 text-success" />
                  <span>Automated backup systems with encryption</span>
                </li>
              </ul>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <Icon name="Users" size={24} color="var(--color-primary)" />
                <h4 className="font-semibold text-foreground">Organizational Measures</h4>
              </div>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start">
                  <Icon name="Check" size={16} className="mr-2 mt-0.5 flex-shrink-0 text-success" />
                  <span>Role-based access controls (RBAC)</span>
                </li>
                <li className="flex items-start">
                  <Icon name="Check" size={16} className="mr-2 mt-0.5 flex-shrink-0 text-success" />
                  <span>Employee training on data protection</span>
                </li>
                <li className="flex items-start">
                  <Icon name="Check" size={16} className="mr-2 mt-0.5 flex-shrink-0 text-success" />
                  <span>Confidentiality agreements with staff</span>
                </li>
                <li className="flex items-start">
                  <Icon name="Check" size={16} className="mr-2 mt-0.5 flex-shrink-0 text-success" />
                  <span>Incident response procedures</span>
                </li>
                <li className="flex items-start">
                  <Icon name="Check" size={16} className="mr-2 mt-0.5 flex-shrink-0 text-success" />
                  <span>Regular privacy impact assessments</span>
                </li>
                <li className="flex items-start">
                  <Icon name="Check" size={16} className="mr-2 mt-0.5 flex-shrink-0 text-success" />
                  <span>Data minimization principles</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 mt-6">
            <div className="flex items-start space-x-3">
              <Icon name="ShieldCheck" size={24} color="var(--color-primary)" className="flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-foreground mb-2">Data Breach Notification</h4>
                <p className="text-sm text-muted-foreground mb-2">
                  In the unlikely event of a data breach affecting your personal data, we will:
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                  <li>Notify supervisory authorities within 72 hours (if required)</li>
                  <li>Notify affected individuals without undue delay</li>
                  <li>Provide information about the nature of the breach and remedial actions</li>
                  <li>Document all breaches for regulatory compliance</li>
                </ul>
              </div>
            </div>
          </div>
        </>
      )
    },
    {
      id: 'retention-deletion',
      title: 'Data Retention & Deletion',
      icon: 'Clock',
      content: (
        <>
          <p className="mb-6">
            We retain personal data only as long as necessary for the purposes outlined in this policy or as required by law:
          </p>

          <div className="space-y-4">
            <div className="bg-card border border-border rounded-lg p-6">
              <h4 className="font-semibold text-foreground mb-3">Account Data</h4>
              <p className="text-sm text-muted-foreground">
                <strong>Retention:</strong> While your account is active + 30 days after account deletion
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                <strong>Deletion Process:</strong> Automatic purge after retention period, irreversible
              </p>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <h4 className="font-semibold text-foreground mb-3">Financial Records</h4>
              <p className="text-sm text-muted-foreground">
                <strong>Retention:</strong> 7 years from transaction date (tax and accounting requirements)
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                <strong>Legal Basis:</strong> Legal obligation, cannot be deleted earlier
              </p>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <h4 className="font-semibold text-foreground mb-3">Analytics & Logs</h4>
              <p className="text-sm text-muted-foreground">
                <strong>Retention:</strong> 26 months (Google Analytics), 90 days (server logs)
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                <strong>Anonymization:</strong> IP addresses anonymized, aggregate data retained indefinitely
              </p>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <h4 className="font-semibold text-foreground mb-3">Content & Projects</h4>
              <p className="text-sm text-muted-foreground">
                <strong>Retention:</strong> Until manual deletion by user or account termination
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                <strong>Backup Copies:</strong> Removed from backups within 90 days of deletion
              </p>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <h4 className="font-semibold text-foreground mb-3">Marketing Communications</h4>
              <p className="text-sm text-muted-foreground">
                <strong>Retention:</strong> Until opt-out or account deletion
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                <strong>Suppression List:</strong> Email addresses retained indefinitely to honor opt-out requests
              </p>
            </div>
          </div>

          <div className="bg-muted/50 border border-border rounded-lg p-6 mt-6">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-foreground mb-2">Automated Deletion</h4>
                <p className="text-sm text-muted-foreground">
                  Our systems automatically purge personal data according to these retention schedules. You can request immediate deletion by exercising your "Right to Erasure" (subject to legal retention requirements).
                </p>
              </div>
            </div>
          </div>
        </>
      )
    },
    {
      id: 'supervisory-authority',
      title: 'Supervisory Authority & Complaints',
      icon: 'Scale',
      content: (
        <>
          <p className="mb-6">
            If you believe we have not adequately addressed your GDPR rights or concerns, you have the right to lodge a complaint with a supervisory authority.
          </p>

          <div className="bg-card border border-border rounded-lg p-6 mb-6">
            <div className="flex items-start space-x-3 mb-4">
              <Icon name="MapPin" size={24} color="var(--color-primary)" className="flex-shrink-0" />
              <div className="flex-1">
                <h4 className="font-semibold text-foreground mb-2">Lead Supervisory Authority</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  For EU/EEA residents, you can contact your local data protection authority or the Irish Data Protection Commission (as we use EU-based infrastructure providers):
                </p>
                <div className="space-y-2 text-sm">
                  <p className="text-muted-foreground">
                    <strong>Irish Data Protection Commission</strong>
                  </p>
                  <p className="text-muted-foreground">
                    21 Fitzwilliam Square South<br />
                    Dublin 2, D02 RD28<br />
                    Ireland
                  </p>
                  <p className="text-muted-foreground">
                    <strong>Website:</strong>{' '}
                    <a href="https://www.dataprotection.ie" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      www.dataprotection.ie
                    </a>
                  </p>
                  <p className="text-muted-foreground">
                    <strong>Email:</strong>{' '}
                    <a href="mailto:info@dataprotection.ie" className="text-primary hover:underline">
                      info@dataprotection.ie
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-6 mb-6">
            <div className="flex items-start space-x-3">
              <Icon name="Globe" size={24} color="var(--color-primary)" className="flex-shrink-0" />
              <div className="flex-1">
                <h4 className="font-semibold text-foreground mb-2">Other European Data Protection Authorities</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  You also have the right to contact the supervisory authority in your country of residence, place of work, or where an alleged infringement occurred.
                </p>
                <p className="text-sm text-muted-foreground">
                  <strong>Find Your Authority:</strong>{' '}
                  <a 
                    href="https://edpb.europa.eu/about-edpb/about-edpb/members_en" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-primary hover:underline"
                  >
                    European Data Protection Board - Member Authorities
                  </a>
                </p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <Icon name="MessageSquare" size={24} color="var(--color-primary)" className="flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-foreground mb-2">Contact Us First</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  Before lodging a complaint with a supervisory authority, we encourage you to contact us directly. We are committed to resolving any concerns you may have about our data practices.
                </p>
                <p className="text-sm text-muted-foreground">
                  <strong>Email:</strong>{' '}
                  <a href="mailto:success@entrepreneurpowernetwork.com" className="text-primary hover:underline">
                    success@entrepreneurpowernetwork.com
                  </a><br />
                  <strong>Subject Line:</strong> "GDPR Complaint" or "Privacy Concern"
                </p>
              </div>
            </div>
          </div>
        </>
      )
    },
    {
      id: 'children-privacy',
      title: "Children\'s Privacy",
      icon: 'Baby',
      content: (
        <>
          <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg p-6 mb-6">
            <div className="flex items-start space-x-3">
              <Icon name="AlertTriangle" size={24} color="var(--color-destructive)" className="flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-foreground mb-2">Age Restriction</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  AuthorPreneur AI is not intended for children under the age of 16. We do not knowingly collect or process personal data from individuals under 16 years old.
                </p>
                <p className="text-sm text-muted-foreground">
                  If you are a parent or guardian and believe your child has provided us with personal data, please contact us immediately at{' '}
                  <a href="mailto:success@entrepreneurpowernetwork.com" className="text-primary hover:underline">
                    success@entrepreneurpowernetwork.com
                  </a>
                </p>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-6">
            <h4 className="font-semibold text-foreground mb-3">Discovery of Underage Users</h4>
            <p className="text-sm text-muted-foreground mb-3">
              If we become aware that we have collected personal data from a child under 16 without parental consent, we will:
            </p>
            <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
              <li>Immediately suspend the account</li>
              <li>Delete all associated personal data within 72 hours</li>
              <li>Remove data from backup systems within 30 days</li>
              <li>Notify supervisory authorities if required by law</li>
            </ul>
          </div>

          <div className="bg-muted/50 border border-border rounded-lg p-6 mt-6">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-foreground mb-2">Age Verification</h4>
                <p className="text-sm text-muted-foreground">
                  During registration, users must confirm they are at least 16 years old. We rely on this self-declaration and take appropriate action when we learn otherwise.
                </p>
              </div>
            </div>
          </div>
        </>
      )
    },
    {
      id: 'contact-dpo',
      title: 'Contact & Data Protection Officer',
      icon: 'Mail',
      content: (
        <>
          <p className="mb-6">
            For any questions about this GDPR Compliance page or to exercise your data protection rights, please contact us:
          </p>

          <div className="bg-card border border-border rounded-lg p-6 mb-6">
            <div className="flex items-start space-x-4 mb-6">
              <Icon name="Building2" size={24} color="var(--color-primary)" className="flex-shrink-0" />
              <div className="flex-1">
                <h4 className="font-semibold text-foreground mb-3">Company Information</h4>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p><strong>Legal Entity:</strong> Entrepreneur Power Network, LLC</p>
                  <p>
                    <strong>Address:</strong><br />
                    813 N. Main Street, Suite 502<br />
                    McAllen, TX 78501<br />
                    United States
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-start space-x-4 border-t border-border pt-6">
              <Icon name="User" size={24} color="var(--color-primary)" className="flex-shrink-0" />
              <div className="flex-1">
                <h4 className="font-semibold text-foreground mb-3">Data Protection Officer</h4>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>
                    <strong>Email:</strong>{' '}
                    <a href="mailto:success@entrepreneurpowernetwork.com" className="text-primary hover:underline">
                      success@entrepreneurpowernetwork.com
                    </a>
                  </p>
                  <p>
                    <strong>Subject Line:</strong> "GDPR Request" or "Privacy Inquiry"
                  </p>
                  <p className="mt-3">
                    <strong>Response Time:</strong> We aim to respond within 30 days of receiving your request (as required by GDPR Article 12).
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <Link
              to="/privacy-policy"
              className="flex items-center justify-between bg-muted/50 border border-border rounded-lg p-4 hover:bg-muted transition-colors"
            >
              <div>
                <h5 className="font-medium text-foreground mb-1">Privacy Policy</h5>
                <p className="text-xs text-muted-foreground">Full data practices</p>
              </div>
              <Icon name="ArrowRight" size={20} color="var(--color-muted-foreground)" />
            </Link>
            <Link
              to="/cookie-policy"
              className="flex items-center justify-between bg-muted/50 border border-border rounded-lg p-4 hover:bg-muted transition-colors"
            >
              <div>
                <h5 className="font-medium text-foreground mb-1">Cookie Policy</h5>
                <p className="text-xs text-muted-foreground">Cookie usage details</p>
              </div>
              <Icon name="ArrowRight" size={20} color="var(--color-muted-foreground)" />
            </Link>
            <Link
              to="/terms-of-service"
              className="flex items-center justify-between bg-muted/50 border border-border rounded-lg p-4 hover:bg-muted transition-colors"
            >
              <div>
                <h5 className="font-medium text-foreground mb-1">Terms of Service</h5>
                <p className="text-xs text-muted-foreground">Platform terms</p>
              </div>
              <Icon name="ArrowRight" size={20} color="var(--color-muted-foreground)" />
            </Link>
          </div>
        </>
      )
    }
  ];

  return (
    <>
      <Helmet>
        <title>GDPR Compliance - AuthorPreneur AI</title>
        <meta
          name="description"
          content="Learn about AuthorPreneur AI's GDPR compliance and your data protection rights under European data privacy law."
        />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="border-b border-border bg-card sticky top-0 z-50">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="BookOpen" size={24} color="var(--color-primary)" />
                </div>
                <span className="text-xl font-heading font-semibold text-foreground">
                  AuthorPreneur AI
                </span>
              </Link>
              <Link
                to="/legal-pages-hub"
                className="text-sm text-muted-foreground hover:text-primary flex items-center"
              >
                <Icon name="ArrowLeft" size={16} className="mr-1" />
                Back to Legal Hub
              </Link>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="container mx-auto px-4 py-12">
          <div className="max-w-6xl mx-auto">
            {/* Page Header */}
            <div className="mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 dark:bg-purple-900/20 rounded-2xl mb-6">
                <Icon name="Lock" size={32} color="var(--color-primary)" />
              </div>
              <h1 className="text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
                GDPR Compliance
              </h1>
              <p className="text-lg text-muted-foreground mb-2">
                Last Updated: December 8, 2025
              </p>
              <p className="text-muted-foreground">
                Effective Date: December 8, 2025
              </p>
            </div>

            <div className="grid lg:grid-cols-4 gap-8">
              {/* Table of Contents - Sidebar */}
              <div className="lg:col-span-1">
                <TableOfContents
                  sections={sections}
                  activeSection={activeSection}
                  onSectionClick={setActiveSection}
                />
              </div>

              {/* Policy Content */}
              <div className="lg:col-span-3 space-y-8">
                {sections?.map((section) => (
                  <PolicySection
                    key={section?.id}
                    section={section}
                    isActive={activeSection === section?.id}
                  />
                ))}

                {/* Quick Actions */}
                <div className="bg-muted/50 border border-border rounded-xl p-6 mt-12">
                  <h3 className="text-lg font-semibold text-foreground mb-4">
                    Quick Actions
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <button className="flex items-center space-x-3 bg-card border border-border p-4 rounded-lg hover:bg-muted transition-colors">
                      <Icon name="Download" size={20} color="var(--color-primary)" />
                      <span className="text-sm font-medium text-foreground">Download PDF</span>
                    </button>
                    <button className="flex items-center space-x-3 bg-card border border-border p-4 rounded-lg hover:bg-muted transition-colors">
                      <Icon name="Mail" size={20} color="var(--color-primary)" />
                      <span className="text-sm font-medium text-foreground">Email Copy</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default GDPRCompliance;